import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDownIcon, SparklesIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, Row, Text } from "@/components";
import { ConfigFormWrapper } from "@/components/companies/shared/ConfigFormWrapper";
import { FormArrayInput } from "@/components/form/FormArrayInput";
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible";

import { useCompanyInternalConfigSubmit } from "@/hooks/updateSets/useCompanyInternalConfigSubmit";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CompanyInternalConfig as CompanyInternalConfigType } from "@/types";

const generalSchema = z.object({
  relatedCompanyNames: z.array(z.string().trim().min(1).max(120)).max(50),
});

export const GeneralSection = ({
  companyInternalConfig,
}: {
  companyInternalConfig: CompanyInternalConfigType;
}) => {
  /* Database CRUD Operations */
  // Update
  const { handleSubmit, isPending: isSaving } = useCompanyInternalConfigSubmit(
    companyInternalConfig.id,
    "General config saved!",
  );

  const form = useForm({
    defaultValues: {
      relatedCompanyNames: companyInternalConfig.relatedCompanyNames,
    },
    resolver: zodResolver(generalSchema),
  });

  /* Render */
  const render = () => {
    return (
      <Collapsible className="group/collapsible">
        <Column className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between px-6 py-4">
            <Row className="items-center gap-2">
              <SparklesIcon className="h-4 w-4 text-slate-500" />
              <Text variant="header">General</Text>
            </Row>
            <ChevronDownIcon className="h-4 w-4 text-slate-500 transition-transform duration-200 group-data-[state=closed]/collapsible:-rotate-90" />
          </CollapsibleTrigger>
          <ConfigFormWrapper
            buttonLabel="Save General Config"
            disabled={isSaving}
            form={form}
            onSubmit={(data) => {
              mixpanelUtils.trackButtonClick(
                "Save General Config",
                MIXPANEL_SCREENS.COMPANY,
              );
              mixpanelUtils.trackFormSubmission(
                "Internal config",
                MIXPANEL_SCREENS.COMPANY,
              );
              handleSubmit(data);
            }}
          >
            <Column className="gap-1">
              <FormArrayInput
                fieldName="relatedCompanyNames"
                form={form}
                label="Related Companies"
                placeholder="e.g. Metal Company Manufacturing"
              />
              <Text className="text-xs font-normal text-slate-500">
                Other names related to the customer: abbreviations, legal names,
                sister companies, etc. The current use case is to prevent
                sourcing from contacting candidates who are currently employed
                by a related company or the company itself, i.e. not poach.
              </Text>
            </Column>
          </ConfigFormWrapper>
        </Column>
      </Collapsible>
    );
  };

  return render();
};
